// ModelLoader.ts

import type * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { ObjectLoader } from 'three'
import localforage from 'localforage'

type TextureSlot =
  | 'map' | 'normalMap' | 'roughnessMap' | 'metalnessMap'
  | 'aoMap' | 'emissiveMap' | 'alphaMap' | 'lightMap'

const TEX_SLOTS: TextureSlot[] = [
  'map', 'normalMap', 'roughnessMap', 'metalnessMap',
  'aoMap', 'emissiveMap', 'alphaMap', 'lightMap',
]

const TEX_MAX_SIZE: Record<TextureSlot, number> = {
  map: 1024,
  normalMap: 512,
  roughnessMap: 512,
  metalnessMap: 512,
  aoMap: 512,
  emissiveMap: 1024,
  alphaMap: 512,
  lightMap: 512,
}

// ─── 纹理去重 ─────────────────────────────────────────────────
export class TextureDeduplicator {
  private hashMap = new Map<string, THREE.Texture>()

  private _hash(image: TexImageSource): string {
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = 16
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(image as CanvasImageSource, 0, 0, 16, 16)
    const data = ctx.getImageData(0, 0, 16, 16).data
    let h = 0
    for (let i = 0; i < data.length; i++)
      h = (h * 31 + data[i]) & 0xFFFFFFFF

    return h.toString(16)
  }

  async run(scene: THREE.Object3D): Promise<number> {
    // 1. 收集所有纹理
    const texMap = new Map<string, THREE.Texture>()
    scene.traverse((obj) => {
      if (!(obj as THREE.Mesh).isMesh)
        return
      const mesh = obj as THREE.Mesh
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      mats.forEach((mat) => {
        TEX_SLOTS.forEach((slot) => {
          const tex = (mat as THREE.MeshStandardMaterial)[slot] as THREE.Texture | null
          if (tex && !texMap.has(tex.uuid))
            texMap.set(tex.uuid, tex)
        })
      })
    })

    // 2. hash对比，找重复
    const replaceMap = new Map<string, THREE.Texture>()
    for (const [uuid, tex] of texMap) {
      if (!tex.image)
        continue
      const hash = this._hash(tex.image as TexImageSource)
      if (this.hashMap.has(hash)) {
        replaceMap.set(uuid, this.hashMap.get(hash)!)
        tex.dispose()
      }
      else {
        this.hashMap.set(hash, tex)
      }
    }

    // 3. 替换重复引用
    scene.traverse((obj) => {
      if (!(obj as THREE.Mesh).isMesh)
        return
      const mesh = obj as THREE.Mesh
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      mats.forEach((mat) => {
        TEX_SLOTS.forEach((slot) => {
          const tex = (mat as THREE.MeshStandardMaterial)[slot] as THREE.Texture | null
          if (tex && replaceMap.has(tex.uuid)) {
            (mat as THREE.MeshStandardMaterial)[slot] = replaceMap.get(tex.uuid) as any
            mat.needsUpdate = true
          }
        })
      })
    })

    return replaceMap.size
  }

  getStats(): { uniqueTextures: number } {
    return { uniqueTextures: this.hashMap.size }
  }
}

// ─── 纹理降采样 ──────────────────────────────────────────────────
function downsampleTextures(root: THREE.Object3D): void {
  const processed = new Set<string>()
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh
    if (!mesh.isMesh)
      return
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    mats.forEach((mat) => {
      TEX_SLOTS.forEach((slot) => {
        const tex = (mat as THREE.MeshStandardMaterial)[slot] as THREE.Texture | null
        if (!tex?.image || processed.has(tex.uuid))
          return
        processed.add(tex.uuid)
        const image = tex.image as HTMLImageElement | HTMLCanvasElement
        const { width, height } = image
        const maxSize = TEX_MAX_SIZE[slot]
        if (width <= maxSize && height <= maxSize)
          return
        const scale = maxSize / Math.max(width, height)
        const canvas = document.createElement('canvas')
        canvas.width = Math.floor(width * scale)
        canvas.height = Math.floor(height * scale)
        canvas.getContext('2d')!.drawImage(
          image as CanvasImageSource, 0, 0, canvas.width, canvas.height,
        )
        tex.image = canvas
        tex.needsUpdate = true
      })
    })
  })
}

// ─── 运行时缓存 ──────────────────────────────────────────────────
interface CacheEntry {
  original: THREE.Object3D // 母本，不加入场景，只用于 clone
  meshes: THREE.Mesh[] // 母本中所有 mesh，clone 时共享
  parserJson: Record<string, any>// 新增
}

class RuntimeModelCache {
  private cache = new Map<string, CacheEntry>()
  private pending = new Map<string, Promise<void>>() // 防并发重复加载

  has(url: string): boolean {
    return this.cache.has(url)
  }

  // 存入（只调用一次）
  set(url: string, original: THREE.Object3D, parserJson: Record<string, any> = {}): void {
    const meshes: THREE.Mesh[] = []
    original.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh)
        meshes.push(obj as THREE.Mesh)
    })
    this.cache.set(url, { original, meshes, parserJson })
  }

  // clone，共享 geometry + material，独立 transform
  clone(url: string): { scene: THREE.Object3D; parser: Record<string, any> } | null {
    const entry = this.cache.get(url)
    if (!entry)
      return null

    const cloned = entry.original.clone(true)
    const srcMeshes = entry.meshes
    let idx = 0

    cloned.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (!mesh.isMesh)
        return
      const src = srcMeshes[idx++]
      if (!src)
        return

      // geometry 共享（显存只有一份）
      mesh.geometry = src.geometry

      // material 独立克隆（各实例互不影响）
      // 内部纹理引用仍共享，不会增加显存
      mesh.material = Array.isArray(src.material)
        ? src.material.map(m => m.clone())
        : src.material.clone()
    })

    return { scene: cloned, parser: { json: { meshes: entry.parserJson } } }
  }

  setPending(url: string, p: Promise<void>): void { this.pending.set(url, p) }
  getPending(url: string): Promise<void> | undefined { return this.pending.get(url) }
  deletePending(url: string): void { this.pending.delete(url) }

  // 释放，所有 clone 之后也不能再使用
  dispose(url: string): void {
    const entry = this.cache.get(url)
    if (!entry)
      return
    entry.meshes.forEach((mesh) => {
      mesh.geometry.dispose()
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      mats.forEach((mat) => {
        TEX_SLOTS.forEach((slot) => {
          const tex = (mat as THREE.MeshStandardMaterial)[slot] as THREE.Texture | null
          tex?.dispose()
        })
        mat.dispose()
      })
    })
    this.cache.delete(url)
    console.log(`[RuntimeCache] 已释放: ${url}`)
  }

  stats() {
    return { cachedModels: this.cache.size }
  }
}

export const runtimeCache = new RuntimeModelCache()

// ─── LoadGLTF 选项类型 ───────────────────────────────────────────
export interface LoadGLTFOptions {
  url: string
  onLoad?: (gltf: GLTF) => void
  onProgress?: (event: ProgressEvent) => void
  onError?: (error: any) => void
  dracoPath?: string
}